// Season form model — shared by the Create Season modal and the season page's Settings tab, so both
// send exactly the same payload to POST / PUT /api/seasons (PUT overwrites every field it lists).

export const PREFIX_COLOR_OPTIONS = [
  { key: 'darkred', name: 'Dark Red', byte: '\u0002', hex: '#b91c1c' },
  { key: 'purple', name: 'Purple', byte: '\u0003', hex: '#a855f7' },
  { key: 'green', name: 'Green', byte: '\u0004', hex: '#84cc16' },
  { key: 'gold', name: 'Gold', byte: '\u0010', hex: '#f59e0b' },
];

export const DEFAULT_PREFIX_COLOR = '\u0004';
const PREFIX_COLOR_LITERAL_MAP = {
  '\\x02': '\u0002',
  '\\x03': '\u0003',
  '\\x04': '\u0004',
  '\\x10': '\u0010',
};

export const normalizePrefixColor = (value) => {
  if (!value) return DEFAULT_PREFIX_COLOR;
  const byKey = PREFIX_COLOR_OPTIONS.find(opt => opt.key === value);
  if (byKey) return byKey.byte;
  const normalized = PREFIX_COLOR_LITERAL_MAP[value] || value;
  return PREFIX_COLOR_OPTIONS.some(opt => opt.byte === normalized) ? normalized : DEFAULT_PREFIX_COLOR;
};

export const getPrefixColorOption = (value) => (
  PREFIX_COLOR_OPTIONS.find(opt => opt.byte === normalizePrefixColor(value)) ||
  PREFIX_COLOR_OPTIONS.find(opt => opt.byte === DEFAULT_PREFIX_COLOR)
);

/** Defaults of a new season (same as the old Create Season modal). */
export function emptySeasonForm() {
  return {
    name: '', best_of: 1, mode: 'active_duty', admin_only_start: true, enforce_teams: false, allow_stop: false, skip_backup_load: false, custom_avatars: false, random_password: false, steam_login_required: false, chat_prefix: '', chat_prefix_color: DEFAULT_PREFIX_COLOR, start_date: '', end_date: '',
    cvars_warmup: [''], cvars_knife_live: [''],
    ts_enabled: false, ts_host: '', ts_query_port: 10011, ts_query_user: '', ts_query_password: '', ts_virtual_server_id: 9987,
    akros_enabled: false, akros_license_key: '',
    hltv_enabled: false, hltv_commands: '',
    veto_enabled: false, veto_style: '', veto_use_coin_flip: false,
    halftime_break_enabled: false, halftime_break_duration: 5, halftime_coach_talk: false,
    show_killfeed: true, stream_delay: 0, cstv1_public: false, cstv2_public: false,
    tech_budget_seconds: 0,
    restore_admin_unpause: false, // after a round restore only an admin can unpause (players' .unpause refused)
    stage_seating: false, // stage seating for this season's matches (Create Match step, match page, API)
    temp_admins: [], // [{ steam_id, name }] - copied into every match of the season
  };
}

const parseCvars = (v) => {
  try {
    const arr = typeof v === 'string' ? JSON.parse(v) : v;
    if (Array.isArray(arr) && arr.length > 0) return [...arr, ''];
  } catch (e) { }
  return [''];
};

/** A season from the API → form state (same as the old modal's edit). */
export function seasonToForm(s) {
  return {
    name: s.name || '',
    best_of: s.best_of || 1,
    mode: s.mode || 'active_duty',
    admin_only_start: !!s.admin_only_start,
    enforce_teams: !!s.enforce_teams,
    allow_stop: !!s.allow_stop,
    skip_backup_load: !!s.skip_backup_load,
    custom_avatars: !!s.custom_avatars,
    random_password: !!s.random_password,
    steam_login_required: !!s.steam_login_required,
    chat_prefix: s.chat_prefix || '',
    chat_prefix_color: normalizePrefixColor(s.chat_prefix_color),
    start_date: s.start_date || '',
    end_date: s.end_date || '',
    cvars_warmup: s.cvars_warmup ? parseCvars(s.cvars_warmup) : [''],
    cvars_knife_live: s.cvars_knife_live ? parseCvars(s.cvars_knife_live) : [''],
    ts_enabled: !!s.ts_enabled,
    ts_host: s.ts_host || '',
    ts_query_port: s.ts_query_port || 10011,
    ts_query_user: s.ts_query_user || '',
    ts_query_password: s.ts_query_password || '', // masked '••••••••' = keep (server side)
    ts_virtual_server_id: s.ts_virtual_server_id || 9987,
    akros_enabled: !!s.akros_enabled,
    akros_license_key: s.akros_license_key || '',
    hltv_enabled: !!s.hltv_enabled,
    hltv_commands: s.hltv_commands || '',
    veto_enabled: !!s.veto_enabled,
    veto_style: s.veto_style || '',
    veto_use_coin_flip: !!s.veto_use_coin_flip,
    halftime_break_enabled: !!s.halftime_break_enabled,
    halftime_break_duration: s.halftime_break_duration || 5,
    halftime_coach_talk: !!s.halftime_coach_talk,
    tech_budget_seconds: s.tech_budget_seconds || 0,
    restore_admin_unpause: !!s.restore_admin_unpause,
    stage_seating: !!s.stage_seating,
    show_killfeed: (s.show_killfeed === undefined || s.show_killfeed === null) ? true : !!s.show_killfeed,
    stream_delay: s.stream_delay || 0,
    cstv1_public: !!s.cstv1_public,
    cstv2_public: !!s.cstv2_public,
    temp_admins: Array.isArray(s.temp_admins) ? s.temp_admins.map(a => ({ steam_id: a.steam_id, name: a.name || '' })) : [],
  };
}

/** Form state → request body for POST / PUT /api/seasons (identical to the old modal).
 *  pendingTempAdmin: the typed-but-not-added temp admin, so it isn't lost on Save. */
export function formToPayload(formData, pendingTempAdmin) {
  const pending = pendingTempAdmin && pendingTempAdmin.steam_id && pendingTempAdmin.steam_id.trim()
    ? [{ steam_id: pendingTempAdmin.steam_id.trim(), name: (pendingTempAdmin.name || '').trim() }] : [];
  return {
    name: formData.name.trim(),
    best_of: formData.best_of,
    mode: formData.mode,
    admin_only_start: formData.admin_only_start,
    enforce_teams: formData.enforce_teams,
    allow_stop: formData.allow_stop,
    skip_backup_load: formData.skip_backup_load,
    custom_avatars: formData.custom_avatars,
    random_password: formData.random_password,
    steam_login_required: formData.steam_login_required,
    chat_prefix: formData.chat_prefix || undefined,
    chat_prefix_color: normalizePrefixColor(formData.chat_prefix_color),
    start_date: formData.start_date,
    end_date: formData.end_date,
    cvars_warmup: (formData.cvars_warmup || []).filter(c => c.trim()),
    cvars_knife_live: (formData.cvars_knife_live || []).filter(c => c.trim()),
    ts_enabled: formData.ts_enabled,
    ts_host: formData.ts_host || undefined,
    ts_query_port: formData.ts_query_port || 10011,
    ts_query_user: formData.ts_query_user || undefined,
    ts_query_password: formData.ts_query_password || undefined,
    ts_virtual_server_id: formData.ts_virtual_server_id || 9987,
    akros_enabled: formData.akros_enabled,
    akros_license_key: formData.akros_license_key || undefined,
    hltv_enabled: formData.hltv_enabled,
    hltv_commands: formData.hltv_commands || undefined,
    veto_enabled: formData.veto_enabled,
    veto_style: formData.veto_style || '',
    veto_use_coin_flip: formData.veto_use_coin_flip,
    halftime_break_enabled: formData.halftime_break_enabled,
    halftime_break_duration: formData.halftime_break_duration,
    halftime_coach_talk: formData.halftime_coach_talk,
    tech_budget_seconds: formData.tech_budget_seconds || 0,
    restore_admin_unpause: !!formData.restore_admin_unpause,
    stage_seating: !!formData.stage_seating,
    show_killfeed: formData.show_killfeed,
    stream_delay: parseInt(formData.stream_delay, 10) || 0,
    cstv1_public: formData.cstv1_public,
    cstv2_public: formData.cstv2_public,
    temp_admins: [...(formData.temp_admins || []), ...pending]
      .map(a => ({ steam_id: a.steam_id, name: (a.name || '').trim() || undefined })),
  };
}

/** Error text for a failed save (codes from the server → translated). */
export function saveErrorText(err, t) {
  const d = err && err.response && err.response.data;
  if (d && d.code === 'bad_steam_id') return t('seasons.tempAdminBadSteam', 'Temp admin: could not resolve the Steam ID "{{input}}".', { input: d.input || '' });
  if (d && d.code === 'too_many_temp_admins') return t('seasons.tempAdminTooMany', 'A season can have at most 20 temp admins.');
  return (d && d.error) || t('seasons.saveFailed', 'Failed to save season');
}

/** 'upcoming' | 'ongoing' | 'finished' from the season's dates (finished = the old list's rule). */
export function seasonPhase(s) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (s.end_date) { const end = new Date(s.end_date); end.setHours(23, 59, 59, 999); if (end < today) return 'finished'; }
  if (s.start_date) { const start = new Date(s.start_date); start.setHours(0, 0, 0, 0); if (start > today) return 'upcoming'; }
  return 'ongoing';
}
